import React, {useEffect} from "react";
import {Box, Text, View} from "@unibuddy/patron";
import {useBooleanFeatureFlag} from "ub-feature-flagging-react";
import {useApolloClient} from "@apollo/client";
import {useHistory, useLocation} from "react-router-dom";
import {useTranslation} from "@unibuddy/intl";
import find from "lodash/find";

import {SSO_ENABLED} from "shared/constants";
import useAnalytics from "shared/AnalyticsNew/hooks/useAnalytics";
import {pageNames, sharedEvents} from "shared/AnalyticsNew/constants/productSpecification";
import {rootURL} from "shared/Utils/WhereAmI";
import {clearAuth, setMe, setToken} from "shared/Auth/actions/authActions";
import getTokenQuery from "shared/Auth/queries/getTokenQuery";

import {getReduxDispatch} from "../../../../App";
import {GoogleAuthButton} from "./GoogleAuthButton";
import {triggerSSOPopup} from "../../SSO/SSOTrigger";
import {useUniversity} from "../../UniversityProvider/UniversityProvider";
import {AuthFormError} from "../../../../components/AuthFormError/AuthFormError";
import {getSSOErrorUrl, setSSOCtaUrl} from "../../SSO/utils";
import {IntlNamespaces} from "../../../../translations/types";
import {
    INTL_CONTINUE_WITH_EMAIL,
    INTL_SIGN_IN_GOOGLE,
    INTL_SIGN_IN_GOOGLE_ERROR,
} from "../../../../translations/login";
import {INTL_APPLICANTS_ONLY} from "../../../../translations/validations";
import {LoginPageForm} from "./LoginPageForm";
import {MeField} from "../../../../types/generated";

function isLoggedInWithRole(userRole: string, userToken: string, targetRole: string) {
    const anyRole = userRole ?? false;
    const token = userToken ?? false;
    const goodRole = targetRole ? anyRole === targetRole : anyRole;
    return goodRole && !!token;
}

export function LoginPageContent() {
    const {slug: universitySlug, features} = useUniversity();
    const {t} = useTranslation([IntlNamespaces.SIGNUP, IntlNamespaces.LOGIN]);
    const isSSOEnabled = useBooleanFeatureFlag(SSO_ENABLED.key, SSO_ENABLED.value);
    const {trackEvent, setUser} = useAnalytics();
    const client = useApolloClient();
    const dispatch = getReduxDispatch();
    const location = useLocation();
    const history = useHistory();
    const SSO_BUTTON_TEXT = "Sign in with Google";
    const hasSSOError = location.pathname === getSSOErrorUrl();
    const applicantsOnlyErrorText = t(
        `${IntlNamespaces.VALIDATIONS}:${INTL_APPLICANTS_ONLY.key}`,
        INTL_APPLICANTS_ONLY.defaultValue,
    );

    function shouldRedirectToAgeVerification(me: MeField) {
        const ageVerificationInfo = me?.applicant?.ageVerificationInfo;
        const isApplicantUniversitySetup = !!find(me?.applicant?.applicantUniversities ?? [], {
            university: {slug: universitySlug},
            setupComplete: true,
        });
        const ageVerificationR1Enabled = features.get("ageVerificationR1");
        return ageVerificationR1Enabled && !isApplicantUniversitySetup && !ageVerificationInfo;
    }

    useEffect(() => {
        // We want this to run once on mount.
        // We also want to make sure it doesn't run again
        // when we try to login in the handleSubmit.
        // Therefore we have an empty deps array
        dispatch(clearAuth());
        trackEvent(sharedEvents.loginPageViewed.name, {
            search: universitySlug,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = async (email: string, password: string): Promise<void> => {
        trackEvent(sharedEvents.loginButtonClicked.name, {
            uniSlug: universitySlug,
        });
        dispatch(clearAuth());

        try {
            const {data} = await client.query({
                query: getTokenQuery,
                variables: {
                    email,
                    password,
                    redirectUrl: location.pathname,
                    // TODO: ONIX_URL is not grabbing an environment variable. Do we need it?
                    rootUrl: "ONIX_URL",
                },
            });

            const accessToken = data.getToken.accessToken;
            const me: MeField = data.getToken.me;

            const isApplicant = isLoggedInWithRole(
                me.anyUser.accountRole,
                accessToken as string,
                "applicant",
            );

            // Set analytics user
            setUser({
                id: me.anyUser.id,
                accountRole: me.anyUser.accountRole,
            });

            if (isApplicant) {
                dispatch(setToken(accessToken));
                dispatch(setMe(me));
                const baseURL = rootURL(location.pathname);
                const hasNextPath = location?.state?.nextPathname;
                const path = hasNextPath ?? `${baseURL}/buddies`;
                if (shouldRedirectToAgeVerification(me)) {
                    return history.replace({
                        pathname: `${baseURL}/auth/verify/age`,
                        search: location.search,
                    });
                }
                history.replace({pathname: path, search: location.search});
            } else {
                throw new Error(applicantsOnlyErrorText);
            }
        } catch (error) {
            console.error(error);
            const message: string | null =
                Array.isArray(error?.graphQLErrors) && error.graphQLErrors.length > 0
                    ? error.graphQLErrors[0].message
                    : null;
            if (message?.includes("locked")) {
                history.push("reset/accountlocked");
            } else {
                // clearAuth() can cause page to reload, error messages may not be seen
                dispatch(clearAuth());
                if (message) {
                    throw new Error(message);
                } else {
                    throw error;
                }
            }
        }
    };

    useEffect(() => {
        if (isSSOEnabled) {
            trackEvent(sharedEvents.ssoLoginButtonVisible.name, {
                universitySlug,
                page: pageNames.login,
            });
        }
    }, [isSSOEnabled, trackEvent, universitySlug]);

    return (
        <View gap="medium-large" minW="100%">
            {isSSOEnabled && (
                <View gap="medium-large">
                    <GoogleAuthButton
                        buttonText={t(
                            `${IntlNamespaces.LOGIN}:${INTL_SIGN_IN_GOOGLE.key}`,
                            SSO_BUTTON_TEXT,
                        )}
                        onClick={() => {
                            trackEvent(sharedEvents.ssoButtonClicked.name, {
                                universitySlug,
                                cta: SSO_BUTTON_TEXT,
                                page: pageNames.login,
                            });
                            setSSOCtaUrl(window.location.pathname);
                            triggerSSOPopup(universitySlug);
                        }}
                    />
                    {hasSSOError && (
                        <AuthFormError>
                            {t(
                                `${IntlNamespaces.LOGIN}:${INTL_SIGN_IN_GOOGLE_ERROR.key}`,
                                INTL_SIGN_IN_GOOGLE_ERROR.defaultValue,
                            )}
                        </AuthFormError>
                    )}
                    <Box display="flex" gap="small" alignItems="center">
                        <Box borderBottomWidth={1} borderColor="grey300" flexGrow={1} />
                        <Text color="grey600" transform="uppercase" size="xsmall">
                            {t(
                                `${IntlNamespaces.LOGIN}:${INTL_CONTINUE_WITH_EMAIL.key}`,
                                INTL_CONTINUE_WITH_EMAIL.defaultValue,
                            )}
                        </Text>
                        <Box borderBottomWidth={1} borderColor="grey300" flexGrow={1} />
                    </Box>
                </View>
            )}
            <LoginPageForm handleSubmit={handleSubmit} />
        </View>
    );
}
